/**
 * PackageTopUp — #1157 E2.6 (Ted #32b, his Item 32.7).
 *
 * Ted: "In Gingr, financials live on the parent's profile: view package
 * balance and usage history, manually add/deduct days." AC: "I add 10 days to
 * [a client's] package with a reason and it shows in History."
 *
 * Two controls beside Correct balance, for staff with the desk write
 * permission (the parent only mounts this when `canWrite`):
 *   • Add days — the package GROWS by N days (total and remaining). Not a
 *     restore: a restore puts back a day that was used and stops at the
 *     package's size. A reason is required; History prints it.
 *   • Expiry — set the last day the package can be used, or remove it. Past
 *     that day the package is refused at checkout and at settle until staff
 *     move the date. A reason is required.
 *
 * The server writes the History row in the same transaction as the change;
 * this component only collects the input and reports the server's answer.
 */
import { useState, type FormEvent, type JSX } from 'react';
import { Alert } from '@ferro/ui';
import { adminPaymentsApi } from '../../lib/api.js';
import type { CustomerPackage } from './PackageBalance.js';

export interface PackageTopUpProps {
  readonly apiUrl: string;
  readonly pkg: CustomerPackage;
  /** Called with a one-line confirmation after the server accepted a change. */
  readonly onDone: (message: string) => void | Promise<void>;
}

type Mode = 'closed' | 'add' | 'expiry';

function dayWord(n: number): string {
  return `${n} day${n === 1 ? '' : 's'}`;
}

/** `YYYY-MM-DD` → "Jan 31, 2027" (a calendar day, no timezone). */
export function formatExpiry(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function PackageTopUp({ apiUrl, pkg, onDone }: PackageTopUpProps): JSX.Element {
  const suffix = `-${pkg.id}`;
  const [mode, setMode] = useState<Mode>('closed');
  const [days, setDays] = useState('10');
  const [expiresOn, setExpiresOn] = useState(pkg.expiresOn ?? '');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = (next: Mode) => {
    setMode((m) => (m === next ? 'closed' : next));
    setError(null);
    setReason('');
    setDays('10');
    setExpiresOn(pkg.expiresOn ?? '');
  };

  const submitAdd = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (submitting) return;
    const n = Number.parseInt(days, 10);
    if (!Number.isFinite(n) || n <= 0 || n > 365) {
      setError('Enter how many days to add (1 to 365).');
      return;
    }
    if (reason.trim().length < 2) {
      setError('Say why the days are being added — History prints it.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const res = await adminPaymentsApi.addPackageDays(apiUrl, pkg.id, { days: n, reason: reason.trim() });
    setSubmitting(false);
    if (res.error || !res.data) {
      setError(res.error ?? 'Could not add the days. Nothing was changed.');
      return;
    }
    setMode('closed');
    await onDone(`Added ${dayWord(n)} — ${dayWord(res.data.package.remainingDays)} remaining.`);
  };

  const submitExpiry = async (clear: boolean): Promise<void> => {
    if (submitting) return;
    if (!clear && !/^\d{4}-\d{2}-\d{2}$/.test(expiresOn)) {
      setError('Pick the last day the package can be used.');
      return;
    }
    if (reason.trim().length < 2) {
      setError('Say why the expiry is changing — History prints it.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const res = await adminPaymentsApi.setPackageExpiry(apiUrl, pkg.id, {
      expiresOn: clear ? null : expiresOn,
      reason: reason.trim(),
    });
    setSubmitting(false);
    if (res.error || !res.data) {
      setError(res.error ?? 'Could not change the expiry. Nothing was changed.');
      return;
    }
    setMode('closed');
    const now = res.data.package.expiresOn;
    await onDone(now ? `Expiry set to ${formatExpiry(now)}.` : 'Expiry removed — the package no longer expires.');
  };

  return (
    <div className="ferro-package-topup" data-testid={`package-topup${suffix}`}>
      <div className="ferro-package-topup__actions">
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          aria-expanded={mode === 'add'}
          onClick={() => open('add')}
          data-testid={`package-add-days-toggle${suffix}`}
        >
          {mode === 'add' ? 'Cancel' : 'Add days'}
        </button>
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          aria-expanded={mode === 'expiry'}
          onClick={() => open('expiry')}
          data-testid={`package-expiry-toggle${suffix}`}
        >
          {mode === 'expiry' ? 'Cancel' : pkg.expiresOn ? 'Change expiry' : 'Set expiry'}
        </button>
      </div>

      {mode !== 'closed' ? (
        <form
          className="ferro-form ferro-stack ferro-package-history__form"
          onSubmit={(e) => {
            if (mode === 'add') void submitAdd(e);
            else {
              e.preventDefault();
              void submitExpiry(false);
            }
          }}
          data-testid={`package-${mode === 'add' ? 'add-days' : 'expiry'}-form${suffix}`}
        >
          {error ? (
            <Alert variant="error">
              <span data-testid={`package-topup-error${suffix}`}>{error}</span>
            </Alert>
          ) : null}
          {mode === 'add' ? (
            <>
              <p className="ferro-field__hint">
                Adds days to this package — it grows from {pkg.totalDays} to{' '}
                {pkg.totalDays + (Number.parseInt(days, 10) || 0)} days. To put back a day used in error, use Correct
                balance instead.
              </p>
              <label className="ferro-field">
                <span className="ferro-field__label">Days to add</span>
                <input
                  className="ferro-field__input ferro-package-history__days"
                  type="number"
                  min={1}
                  max={365}
                  inputMode="numeric"
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  data-testid={`package-add-days-days${suffix}`}
                />
              </label>
            </>
          ) : (
            <>
              <p className="ferro-field__hint">
                {pkg.expiresOn
                  ? `Expires ${formatExpiry(pkg.expiresOn)} now. After the last day it is refused at checkout until the date moves.`
                  : 'No expiry now. After the last day it is refused at checkout until the date moves.'}
              </p>
              <label className="ferro-field">
                <span className="ferro-field__label">Last day it can be used</span>
                <input
                  className="ferro-field__input ferro-package-history__date"
                  type="date"
                  value={expiresOn}
                  onChange={(e) => setExpiresOn(e.target.value)}
                  data-testid={`package-expiry-date${suffix}`}
                />
              </label>
            </>
          )}
          <label className="ferro-field">
            <span className="ferro-field__label">Reason (required)</span>
            <input
              className="ferro-field__input"
              type="text"
              maxLength={200}
              value={reason}
              placeholder={mode === 'add' ? 'e.g. 10 extra days paid at the desk' : 'e.g. extended as a courtesy'}
              onChange={(e) => setReason(e.target.value)}
              data-testid={`package-topup-reason${suffix}`}
            />
          </label>
          <div className="ferro-package-topup__actions">
            <button
              type="submit"
              className="ferro-btn ferro-btn--primary"
              disabled={submitting}
              data-testid={`package-topup-submit${suffix}`}
            >
              {submitting
                ? 'Saving…'
                : mode === 'add'
                  ? `Add ${dayWord(Number.parseInt(days, 10) || 0)}`
                  : 'Set expiry'}
            </button>
            {mode === 'expiry' && pkg.expiresOn ? (
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                disabled={submitting}
                onClick={() => void submitExpiry(true)}
                data-testid={`package-expiry-clear${suffix}`}
              >
                Remove expiry
              </button>
            ) : null}
          </div>
        </form>
      ) : null}
    </div>
  );
}
